import useApi, {useApollo} from "/@src/composable/useApi";
import {Maybe} from "graphql/jsutils/Maybe";
import {Content, Detail, Exact, Scalars, Tag, TagRelation} from "/@src/models/graphApi";
import {FindContentsDocument, FindContentsFullDocument} from "/@src/api/contentRepo";
import {ref} from "vue";
import {ContentKind, ItemsResponse, TagGroup} from "/@src/models/api";
import {ContentLevel, ContentStatusValue, TagData} from "/@src/models/content";
import useArticle from "/@src/composable/content/useArticle";
import useLinkContent from "/@src/composable/content/useLinkContent";
import useVideo from "/@src/composable/content/useVideo";
import {toISOLocal} from "/@src/utils/date";

export type FindContentsQuery = { __typename?: 'query', contents?: { __typename?: 'ContentPage', total_count?: number | null | undefined, items?: Array<{ __typename?: 'Content', raw_id?: number | null | undefined, title?: string | null | undefined, cover_url?: string | null | undefined, description?: string | null | undefined, status?: string | null | undefined, vip_visibility?: number | null | undefined, datetime?: any | null | undefined, kind?: number | null | undefined, counter?: { __typename?: 'Counter', viewed?: number | null | undefined, commented?: number | null | undefined } | null | undefined, author?: { __typename?: 'Author', id?: number | null | undefined, nickname?: string | null | undefined, avatar?: string | null | undefined } | null | undefined, tags?: Array<{ __typename?: 'Tag', id?: number | null | undefined, name?: string | null | undefined, is_topic?: boolean | null | undefined } | null | undefined> | null | undefined, detail?: { __typename?: 'Article', id?: number | null | undefined, title?: string | null | undefined, tag_relations?: Array<{ __typename?: 'TagRelation', id?: number | null | undefined, group?: number | null | undefined } | null | undefined> | null | undefined } | { __typename?: 'Video', id?: number | null | undefined, title?: string | null | undefined, planned_published_at?: any | null | undefined, tags?: Array<{ __typename?: 'Tag', id?: number | null | undefined } | null | undefined> | null | undefined, tag_relations?: Array<{ __typename?: 'TagRelation', id?: number | null | undefined, group?: number | null | undefined } | null | undefined> | null | undefined, episodes?: Array<{ __typename?: 'VideoEpisode', duration?: number | null | undefined } | null | undefined> | null | undefined } | { __typename?: 'Audio' } | { __typename?: 'AudioEpisode', id?: number | null | undefined, name?: string | null | undefined, tag_relations?: Array<{ __typename?: 'TagRelation', id?: number | null | undefined, group?: number | null | undefined } | null | undefined> | null | undefined } | { __typename?: 'Post' } | { __typename?: 'Report', id?: number | null | undefined, title?: string | null | undefined } | { __typename?: 'Comment' } | { __typename?: 'Roadshow', id?: number | null | undefined, title?: string | null | undefined, tag_relations?: Array<{ __typename?: 'TagRelation', id?: number | null | undefined, group?: number | null | undefined } | null | undefined> | null | undefined } | { __typename?: 'Link' } | { __typename?: 'Faq' } | { __typename?: 'RoadMap' } | null | undefined } | null | undefined> | null | undefined } | null | undefined };
export type FindContentsQueryVariables = Exact<{
  first?: Maybe<Scalars['Int']>;
  offset?: Maybe<Scalars['Int']>;
  group?: Maybe<Scalars['Int']>;
  tag_id?: Maybe<Scalars['Int']>;
  vip_visibility?: Maybe<Scalars['Int']>;
  tags?: Maybe<Array<Maybe<Scalars['Int']>> | Maybe<Scalars['Int']>>;
  user_id?: Maybe<Scalars['Int']>;
  track?: Maybe<Scalars['String']>;
  after?: Maybe<Scalars['String']>;
  kinds?: Maybe<Array<Maybe<Scalars['Int']>> | Maybe<Scalars['Int']>>;
  status?: Maybe<ContentStatusValue>;
  start_time?: Maybe<Scalars['Timestamp']>;
  end_time?: Maybe<Scalars['Timestamp']>;
  //是否获取所有字段（用户列表处获取完整数据时）
  isFullMode?:boolean
}>;
export type ContentsQueryParams = {
  first?:number
  offset?:number
  group?:number
  tagId?:number
  tags?:number[]
  track?:string
  sortBy?:string
  userId?:number
  contentLevel?:ContentLevel
  kinds?:number[]
  status?:ContentStatusValue
  startTime?:Date
  endTime?:Date
  nextCursor?:string
  //是否获取所有字段（用户列表处获取完整数据时）
  isFullMode?:boolean
  nextPage?:boolean
  wordsLessThan?:number
  wordsGreaterThan?:number
}
export default function useContents() {
  const api = useApi()
  const graph = useApollo()
  const {deleteArticleContent} = useArticle()
  const {deleteLink} = useLinkContent()
  const {deleteVideoContent} = useVideo()
  const contentListResponse = ref<ItemsResponse<Content>>()
  contentListResponse.value={
    items:[],
    total_count:0
  }
  const contentsLoading = ref(false)
  contentListResponse.value={
    items:[],
    total_count:0
  }
  const queryContents = async (viewParams: ContentsQueryParams)=>{
    contentsLoading.value = true
    const query = viewParams.isFullMode?FindContentsFullDocument:FindContentsDocument
    const params:FindContentsQueryVariables = {
      first:viewParams.first,
      offset:viewParams.offset,
      group:viewParams.group,
      track:viewParams.track,
      sortBy:viewParams.sortBy,
      after:viewParams.nextCursor,
      vip_visibility:viewParams.contentLevel,
      tags:viewParams.tags,
      kinds:viewParams.kinds,
      status:viewParams.status,
      tag_id:viewParams.tagId,
      user_id:viewParams.userId,
    }
    if(viewParams.startTime){
      params.start_time = toISOLocal(viewParams.startTime)
    }
    if(viewParams.endTime){
      params.end_time = toISOLocal(viewParams.endTime)
    }
    const  res = await graph.query<FindContentsQuery, FindContentsQueryVariables>({query:query,variables:params})
    // 此处通过apollo获取的content中，detail(union)内tag_relation中的group会重复
    // fix: 该问题为apollo缓存导致的问题
    // const  res = await postGraphql<FindContentsQuery>(api,'findContents',FindContentsDocument.loc?.source.body!!,params)
    // contentListResponse.value = _.cloneDeep(res.data.contents) as ItemsResponse<Content>
    // contentListResponse.value = res.data.contents as ItemsResponse<Content>
    const data = res.data
    if(data.contents!.items){
      data.contents!.items?.forEach((value, index) => {
        if(value!.tags&&value!.tags.length>0){
          if(value!.detail){
            const relations = (value!.detail as Detail).tag_relations as Array<TagRelation>
            for(const tag of value!.tags){
              if(relations&&relations.length >0){
                for(const relation of relations){
                  if(relation.id === tag!.id && relation.group === TagGroup.TREASURY){
                    (tag as TagData).treasury = true
                    break
                  }
                }
              }
            }
            value!.detail.tags = value!.tags
          }
        }
      })
    }
    if(viewParams.nextPage){
      contentListResponse.value?.items.push(...data.contents!.items)
    }else {
      contentListResponse.value = {
        total_count:data.contents!!.total_count!!,
        items:data.contents!!.items as Content[]
      }
    }
    if(data.contents!.page_info){
      if(data.contents?.page_info.has_next_page){
        contentListResponse.value!.page_info = data.contents?.page_info
      }else {
        if(contentListResponse.value!.page_info){
          contentListResponse.value!.page_info!.end_cursor = undefined
          contentListResponse.value!.page_info!.has_next_page = false
        }
      }
    }
    contentsLoading.value = false
    return contentListResponse.value
  }
  const removeContent = async (content:Content) => {
    switch (content.kind) {
      case ContentKind.ARTICLE:
      case ContentKind.CALENDAR:
        await deleteArticleContent(content.raw_id!)
        break
      case ContentKind.LINK:
        await deleteLink(content.raw_id!)
        break
      case ContentKind.VIDEO:
        await deleteVideoContent(content.raw_id!)
        break
    }
  }
  return {
    contentListResponse,
    contentsLoading,
    queryContents,
    removeContent
  }
}
